import { useEffect, useState } from 'react'
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Stack, Typography,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useMutation } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { OrgStructureImportRow, OrgStructureImportSummary } from '@cmms/api-client'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'

// Self-contained CSV parser + preview dialog, structurally cloned from
// `ImportSitesDialog` in SitesTab.tsx (same quoted-value handling, same
// row-error-highlighting table) with one added step: after client-side
// parsing, a `previewImport` call resolves every row against existing
// data so the admin sees new/existing/updated counts before anything
// commits. Customer/Site/Location extra columns mirror exactly what
// their single-row create/edit forms expose.

const CSV_HEADERS = [
  'group_name', 'subgroup_name',
  'customer_name', 'customer_slug', 'customer_description', 'customer_status',
  'site_name', 'site_description', 'site_address', 'site_latitude', 'site_longitude',
  'site_entity', 'site_state', 'site_branch_area_sqft', 'site_category',
  'site_field1', 'site_field2', 'site_field3', 'site_field4', 'site_field5',
  'location_name', 'location_type', 'location_parent_name', 'location_description',
] as const
type CsvField = (typeof CSV_HEADERS)[number]

const VALID_LOCATION_TYPES = ['building', 'outdoor', 'floor', 'zone', 'wing', 'room', 'area', 'other']
const VALID_CUSTOMER_STATUSES = ['active', 'inactive']

interface ParsedRow {
  rowNum: number
  group_name?: string
  subgroup_name?: string
  customer_name: string
  customer_slug?: string
  customer_description?: string
  customer_status?: 'active' | 'inactive'
  site_name: string
  site_description?: string
  site_address?: string
  site_latitude?: number
  site_longitude?: number
  site_entity?: string
  site_state?: string
  site_branch_area_sqft?: number
  site_category?: string
  site_field1?: string
  site_field2?: string
  site_field3?: string
  site_field4?: string
  site_field5?: string
  location_name?: string
  location_type?: string
  location_parent_name?: string
  location_description?: string
  error: string | null
}

function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ } else { inQuotes = false }
      } else {
        cur += ch
      }
    } else {
      if (ch === '"' && cur.length === 0) inQuotes = true
      else if (ch === ',') { out.push(cur); cur = '' }
      else cur += ch
    }
  }
  out.push(cur)
  return out
}

function parseOrgStructureCsv(text: string): { rows: ParsedRow[]; headerError: string | null } {
  const cleaned = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n')
  const lines = cleaned.split('\n').filter((l) => l.trim().length > 0)
  if (lines.length === 0) return { rows: [], headerError: 'File is empty' }

  const header = parseCsvLine(lines[0]!).map((h) => h.trim().toLowerCase())
  const colIndex: Partial<Record<CsvField, number>> = {}
  CSV_HEADERS.forEach((field) => {
    const idx = header.indexOf(field)
    if (idx >= 0) colIndex[field] = idx
  })
  if (colIndex.customer_name === undefined || colIndex.site_name === undefined) {
    return {
      rows: [],
      headerError: `Header row must include "customer_name" and "site_name" columns. Found: ${header.join(', ') || '(empty)'}`,
    }
  }

  const rows: ParsedRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]!).map((c) => c.trim())
    const get = (field: CsvField): string => colIndex[field] !== undefined ? (cells[colIndex[field]!] ?? '') : ''

    const groupName = get('group_name')
    const subgroupName = get('subgroup_name')
    const customerName = get('customer_name')
    const customerSlug = get('customer_slug')
    const customerDescription = get('customer_description')
    const customerStatusRaw = get('customer_status')
    const siteName = get('site_name')
    const siteDescription = get('site_description')
    const siteAddress = get('site_address')
    const siteLatRaw = get('site_latitude')
    const siteLngRaw = get('site_longitude')
    const siteEntity = get('site_entity')
    const siteState = get('site_state')
    const siteAreaRaw = get('site_branch_area_sqft')
    const siteCategory = get('site_category')
    const siteField1 = get('site_field1')
    const siteField2 = get('site_field2')
    const siteField3 = get('site_field3')
    const siteField4 = get('site_field4')
    const siteField5 = get('site_field5')
    const locationName = get('location_name')
    const locationType = get('location_type')
    const locationParentName = get('location_parent_name')
    const locationDescription = get('location_description')

    let error: string | null = null
    if (!customerName) error = 'Missing customer_name'
    if (!siteName) error = error ?? 'Missing site_name'
    if (subgroupName && !groupName) error = error ?? 'subgroup_name requires group_name'
    if (groupName && !subgroupName) error = error ?? 'group_name requires subgroup_name'
    if (locationParentName && !locationName) error = error ?? 'location_parent_name requires location_name'
    if (locationName && !locationType) error = error ?? 'location_type is required when location_name is set'
    if (locationType && !VALID_LOCATION_TYPES.includes(locationType.toLowerCase())) {
      error = error ?? `location_type must be one of: ${VALID_LOCATION_TYPES.join(', ')}`
    }

    let customerStatus: 'active' | 'inactive' | undefined
    if (customerStatusRaw) {
      const v = customerStatusRaw.toLowerCase()
      if (VALID_CUSTOMER_STATUSES.includes(v)) customerStatus = v as 'active' | 'inactive'
      else error = error ?? `customer_status must be one of: ${VALID_CUSTOMER_STATUSES.join(', ')}`
    }

    let siteLatitude: number | undefined
    if (siteLatRaw) {
      const n = Number(siteLatRaw)
      if (Number.isFinite(n)) siteLatitude = n
      else error = error ?? `site_latitude "${siteLatRaw}" is not a number`
    }
    let siteLongitude: number | undefined
    if (siteLngRaw) {
      const n = Number(siteLngRaw)
      if (Number.isFinite(n)) siteLongitude = n
      else error = error ?? `site_longitude "${siteLngRaw}" is not a number`
    }
    let siteBranchAreaSqft: number | undefined
    if (siteAreaRaw) {
      const n = Number(siteAreaRaw.replace(/[, ]/g, ''))
      if (Number.isFinite(n) && Number.isInteger(n) && n >= 0) siteBranchAreaSqft = n
      else error = error ?? `site_branch_area_sqft "${siteAreaRaw}" must be a non-negative whole number`
    }

    rows.push({
      rowNum: i + 1,
      group_name: groupName || undefined,
      subgroup_name: subgroupName || undefined,
      customer_name: customerName,
      customer_slug: customerSlug || undefined,
      customer_description: customerDescription || undefined,
      customer_status: customerStatus,
      site_name: siteName,
      site_description: siteDescription || undefined,
      site_address: siteAddress || undefined,
      site_latitude: siteLatitude,
      site_longitude: siteLongitude,
      site_entity: siteEntity || undefined,
      site_state: siteState || undefined,
      site_branch_area_sqft: siteBranchAreaSqft,
      site_category: siteCategory || undefined,
      site_field1: siteField1 || undefined,
      site_field2: siteField2 || undefined,
      site_field3: siteField3 || undefined,
      site_field4: siteField4 || undefined,
      site_field5: siteField5 || undefined,
      location_name: locationName || undefined,
      location_type: locationType || undefined,
      location_parent_name: locationParentName || undefined,
      location_description: locationDescription || undefined,
      error,
    })
  }
  return { rows, headerError: null }
}

function toPayload(rows: ParsedRow[]): OrgStructureImportRow[] {
  return rows.map((r) => ({
    group_name: r.group_name,
    subgroup_name: r.subgroup_name,
    customer_name: r.customer_name,
    customer_slug: r.customer_slug,
    customer_description: r.customer_description,
    customer_status: r.customer_status,
    site_name: r.site_name,
    site_description: r.site_description,
    site_address: r.site_address,
    site_latitude: r.site_latitude,
    site_longitude: r.site_longitude,
    site_entity: r.site_entity,
    site_state: r.site_state,
    site_branch_area_sqft: r.site_branch_area_sqft,
    site_category: r.site_category,
    site_field1: r.site_field1,
    site_field2: r.site_field2,
    site_field3: r.site_field3,
    site_field4: r.site_field4,
    site_field5: r.site_field5,
    location_name: r.location_name,
    location_type: r.location_type,
    location_parent_name: r.location_parent_name,
    location_description: r.location_description,
  }))
}

function downloadCsvTemplate() {
  const header = CSV_HEADERS.join(',')
  const rows = [
    ['North Region', 'Uptown', 'Acme Corp', 'acme-corp', 'Regional distributor', 'active',
      'Acme HQ', 'Primary office', '100 Industrial Rd', '12.9716', '77.5946', 'Acme Operations Ltd', 'Karnataka', '18500', 'Branch',
      'Region East', '', '', '', '', '', '', '', ''],
    ['North Region', 'Uptown', 'Acme Corp', '', '', '',
      'Acme HQ', '', '', '', '', '', '', '', '',
      '', '', '', '', '', 'Main Building', 'building', '', ''],
    ['North Region', 'Uptown', 'Acme Corp', '', '', '',
      'Acme HQ', '', '', '', '', '', '', '', '',
      '', '', '', '', '', 'Floor 1', 'floor', 'Main Building', ''],
    ['', '', 'Standalone Co', '', '', '',
      'Warehouse B', '', '', '', '', '', '', '', '',
      '', '', '', '', '', '', '', '', ''],
  ]
  const csv = header + '\n' + rows.map((r) => r.join(',')).join('\n') + '\n'
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'org-structure-import-template.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

const LEVEL_LABELS = [
  ['Groups', 'groups'], ['Sub Groups', 'subgroups'], ['Customers', 'customers'],
  ['Sites', 'sites'], ['Locations', 'locations'],
] as const

function ColChip({ children }: { children: string }) {
  return (
    <Box component="code" sx={{
      bgcolor: 'action.hover', color: 'text.secondary',
      px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, fontSize: 11,
      whiteSpace: 'nowrap',
    }}>
      {children}
    </Box>
  )
}

export function ImportOrgStructureDialog({ open, onClose, onImported }: {
  open: boolean; onClose: () => void; onImported: () => void
}) {
  const [filename, setFilename] = useState<string | null>(null)
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [headerError, setHeaderError] = useState<string | null>(null)
  const [submitErrors, setSubmitErrors] = useState<Map<number, string>>(new Map())
  const [summary, setSummary] = useState<OrgStructureImportSummary | null>(null)
  const [helpOpen, setHelpOpen] = useState(true)
  // Off by default — matching a row to an existing customer/site/
  // location never touches its fields unless explicitly opted into.
  const [updateExisting, setUpdateExisting] = useState(false)

  function reset() {
    setFilename(null); setRows([]); setHeaderError(null); setSubmitErrors(new Map()); setSummary(null); setUpdateExisting(false)
  }
  useEffect(() => { if (!open) reset() }, [open])

  function applyRowErrors(e: unknown, fallbackMessage: string) {
    if (e instanceof ApiError && e.rowErrors?.length) {
      const m = new Map<number, string>()
      // Server `row` is 1-based against the payload; +1 aligns with the
      // user's spreadsheet, which has a header row above the first data row.
      e.rowErrors.forEach((er) => m.set(er.row + 1, er.message))
      setSubmitErrors(m)
      toast.error('Some rows were rejected — see highlights')
      return
    }
    toast.error(e instanceof ApiError ? e.message : fallbackMessage)
  }

  const previewM = useMutation({
    mutationFn: ({ rs, upd }: { rs: ParsedRow[]; upd: boolean }) => api.orgStructure.previewImport(toPayload(rs), upd),
    onSuccess: (data) => { setSummary(data); setSubmitErrors(new Map()) },
    onError: (e) => applyRowErrors(e, 'Preview failed'),
  })

  const commitM = useMutation({
    mutationFn: ({ rs, upd }: { rs: ParsedRow[]; upd: boolean }) => api.orgStructure.commitImport(toPayload(rs), upd),
    onSuccess: (data) => {
      const total = data.groups.new + data.subgroups.new + data.customers.new + data.sites.new + data.locations.new
      const updated = data.groups.updated + data.subgroups.updated + data.customers.updated + data.sites.updated + data.locations.updated
      const parts = []
      if (total > 0) parts.push(`${total} new`)
      if (updated > 0) parts.push(`${updated} updated`)
      toast.success(parts.length > 0 ? `Imported — ${parts.join(', ')}` : 'Import complete — everything already existed, unchanged')
      onImported()
    },
    onError: (e) => applyRowErrors(e, 'Import failed'),
  })

  async function handleFile(file: File) {
    setSubmitErrors(new Map())
    setSummary(null)
    const text = await file.text()
    const parsed = parseOrgStructureCsv(text)
    setFilename(file.name)
    setRows(parsed.rows)
    setHeaderError(parsed.headerError)
    if (parsed.rows.length > 0 && !parsed.rows.some((r) => r.error)) {
      previewM.mutate({ rs: parsed.rows, upd: updateExisting })
    }
  }

  function handleUpdateExistingChange(checked: boolean) {
    setUpdateExisting(checked)
    // Re-preview so the summary's "updated" counts reflect the new toggle
    // state immediately, rather than only taking effect at commit time.
    if (rows.length > 0 && !rows.some((r) => r.error)) {
      previewM.mutate({ rs: rows, upd: checked })
    }
  }

  const canImport = Boolean(summary) && submitErrors.size === 0 && !commitM.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1 }}>Import Groups, Customers, Sites & Locations</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
          Each row builds (or reuses, if the name already exists) a Group → Sub Group → Customer → Site → Location
          chain. The import is all-or-nothing — if any row is invalid, nothing is created and you can fix the file
          and re-run. Up to 2000 rows per file.
        </Typography>

        <Box sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 1.5, mb: 2, overflow: 'hidden',
          bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
        }}>
          <Box onClick={() => setHelpOpen((v) => !v)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer',
              '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)' },
            }}>
            <HelpOutlineOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>How to build the CSV</Typography>
            {helpOpen
              ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          {helpOpen && (
            <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
              <Stack spacing={1.5}>
                <Typography sx={{ fontSize: 12 }}>
                  <Box component="code" sx={{ bgcolor: (t) => `${t.palette.primary.main}14`, color: 'primary.main', px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, mr: 0.5 }}>customer_name</Box>
                  <Box component="code" sx={{ bgcolor: (t) => `${t.palette.primary.main}14`, color: 'primary.main', px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, mr: 0.5 }}>site_name</Box>
                  <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                    are required on every row — everything else is optional.
                  </Typography>
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.55 }}>
                  A name that already exists (matched case-insensitively) is reused; a new name is created. Set{' '}
                  <strong>group_name</strong> + <strong>subgroup_name</strong> together to place the customer under
                  a Sub Group — leave both blank to leave it ungrouped (subgroup_name alone is rejected, since two
                  different Groups could each have a Sub Group with the same name). Leave{' '}
                  <strong>location_name</strong> blank to just create the Group/Sub Group/Customer/Site chain with
                  no location. To build a location tree (building → floor → room), give each level its own row and
                  set <strong>location_parent_name</strong> to the parent row&apos;s <strong>location_name</strong> —
                  every row still repeats its full customer_name/site_name ancestry. A location&apos;s parent can
                  already exist from an earlier import — it doesn&apos;t need its own row in this file too.
                </Typography>

                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
                    Customer columns (optional)
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <ColChip>customer_slug</ColChip>
                    <ColChip>customer_description</ColChip>
                    <ColChip>customer_status (active/inactive)</ColChip>
                  </Stack>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
                    Site columns (optional)
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <ColChip>site_description</ColChip>
                    <ColChip>site_address</ColChip>
                    <ColChip>site_latitude</ColChip>
                    <ColChip>site_longitude</ColChip>
                    <ColChip>site_entity</ColChip>
                    <ColChip>site_state</ColChip>
                    <ColChip>site_branch_area_sqft</ColChip>
                    <ColChip>site_category</ColChip>
                    <ColChip>site_field1..field5</ColChip>
                  </Stack>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
                    Location columns
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <ColChip>location_name</ColChip>
                    <ColChip>{`location_type (${VALID_LOCATION_TYPES.join('/')})`}</ColChip>
                    <ColChip>location_parent_name</ColChip>
                    <ColChip>location_description</ColChip>
                  </Stack>
                </Box>

                <Typography sx={{ fontSize: 11, color: 'text.disabled', fontStyle: 'italic', lineHeight: 1.5 }}>
                  If the same customer/site/location appears on more than one row, every row that sets a given
                  optional column must agree on its value — conflicting values in the same file are rejected before
                  anything is written.
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Button component="label" variant="outlined" startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />} sx={{ fontSize: 13, fontWeight: 600 }}>
            {filename ? 'Choose another file' : 'Choose CSV file'}
            <input hidden type="file" accept=".csv,text/csv" onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) handleFile(f)
              e.currentTarget.value = ''
            }} />
          </Button>
          <Button variant="text" startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />} onClick={downloadCsvTemplate} sx={{ fontSize: 13, fontWeight: 600 }}>
            Download template
          </Button>
          <Box sx={{ flex: 1 }} />
          <FormControlLabel
            control={<Checkbox size="small" checked={updateExisting} onChange={(e) => handleUpdateExistingChange(e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Update existing records with values from this file
              </Typography>
            }
            sx={{ mr: 0 }}
          />
        </Stack>

        {filename && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.25 }}>
            File: <strong>{filename}</strong> · {rows.length} data row{rows.length === 1 ? '' : 's'}
          </Typography>
        )}

        {headerError && (
          <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: 'error.main', color: '#fff' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{headerError}</Typography>
          </Box>
        )}

        {previewM.isPending && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Checking against existing data…</Typography>
          </Stack>
        )}

        {summary && submitErrors.size === 0 && (
          <Box sx={{
            p: 2, mb: 2, borderRadius: 1.5, border: '1px solid', borderColor: (t) => `${t.palette.success.main}40`,
            bgcolor: (t) => `${t.palette.success.main}0C`,
          }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'success.main' }}>Ready to import</Typography>
            </Stack>
            <Stack spacing={0.5}>
              {LEVEL_LABELS.map(([label, key]) => {
                const s = summary[key]
                if (s.new === 0 && s.existing === 0) return null
                return (
                  <Typography key={key} sx={{ fontSize: 12, color: 'text.secondary' }}>
                    <strong>{label}:</strong> {s.new} new
                    {s.existing > 0 ? `, ${s.existing} already existed (${s.existingNames.slice(0, 5).join(', ')}${s.existingNames.length > 5 ? '…' : ''})` : ''}
                    {updateExisting && s.updated > 0 ? ` — ${s.updated} will be updated (${s.updatedNames.slice(0, 5).join(', ')}${s.updatedNames.length > 5 ? '…' : ''})` : ''}
                  </Typography>
                )
              })}
            </Stack>
          </Box>
        )}

        {rows.length > 0 && (
          <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <Box component="thead" sx={{
                position: 'sticky', top: 0, bgcolor: (t) => t.palette.mode === 'light' ? '#F8FAFC' : 'rgba(255,255,255,0.04)',
                '& th': { textAlign: 'left', fontWeight: 700, color: 'text.secondary', px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' },
              }}>
                <tr>
                  <th style={{ width: 44 }}>Row</th>
                  <th>Group / Sub Group</th>
                  <th>Customer</th>
                  <th>Site</th>
                  <th>Location</th>
                  <th>Issue</th>
                </tr>
              </Box>
              <Box component="tbody" sx={{ '& td': { px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'top' } }}>
                {rows.map((r) => {
                  const serverErr = submitErrors.get(r.rowNum) ?? null
                  const err = r.error ?? serverErr
                  return (
                    <tr key={r.rowNum} style={err ? { background: 'rgba(220,38,38,0.06)' } : undefined}>
                      <td style={{ color: 'rgba(0,0,0,0.5)', fontFamily: 'ui-monospace, monospace' }}>{r.rowNum}</td>
                      <td>{r.group_name ? `${r.group_name} → ${r.subgroup_name}` : <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td style={{ fontWeight: 600 }}>{r.customer_name || <em>—</em>}</td>
                      <td>{r.site_name || <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td>{r.location_name || <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td style={{ color: '#DC2626', fontWeight: err ? 600 : 400 }}>{err ?? ''}</td>
                    </tr>
                  )
                })}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!canImport} onClick={() => commitM.mutate({ rs: rows, upd: updateExisting })} sx={{ fontSize: 13, fontWeight: 600 }}>
          {commitM.isPending ? 'Importing…' : summary ? `Import ${rows.length} row${rows.length === 1 ? '' : 's'}` : 'Choose a file first'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
